import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Popover,
} from '@mui/material';
import SmartphoneOutlinedIcon from '@mui/icons-material/SmartphoneOutlined';
import SecurityIcon from '@mui/icons-material/Security';

interface Props {
  open: boolean;
  onClose: () => void;
  onSelectMethod: (method: 'TOTP' | 'SMS') => void;
  anchorEl?: HTMLElement | null;
  defaultMethod?: 'TOTP' | 'SMS'; // Default method to highlight
  canDisableCompanyMfa?: boolean; // Show option to disable company MFA (for admin/owner)
  onDisableCompanyMfa?: () => void; // Handler to disable company MFA
}

const MfaMethodSelectionDialog: React.FC<Props> = ({ open, onClose, onSelectMethod, anchorEl, defaultMethod = 'TOTP', canDisableCompanyMfa = false, onDisableCompanyMfa }) => {
  const { t } = useTranslation();

  const methodOptions = (
    <>
      <Typography variant="body2" sx={{ mb: 3, color: 'text.secondary', fontSize: '12px' }}>
        {t('sentences:pages.mfa_setup.choose_method_description') || 
          'Select how you want to receive verification codes. You can set up both methods later for added security.'}
      </Typography>

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        {/* Authenticator App Option */}
        <Button
          variant={defaultMethod === 'TOTP' ? "contained" : "outlined"}
          fullWidth
          onClick={() => {
            onSelectMethod('TOTP');
            onClose();
          }}
          sx={{
            p: 2.5,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 1,
            borderColor: defaultMethod === 'TOTP' ? '#1976d2' : '#1976d2',
            backgroundColor: defaultMethod === 'TOTP' ? '#1976d2' : 'transparent',
            color: defaultMethod === 'TOTP' ? '#fff' : '#1976d2',
            '&:hover': {
              borderColor: '#1565c0',
              backgroundColor: defaultMethod === 'TOTP' ? '#1565c0' : '#e3f2fd',
            },
          }}
        >
          <SecurityIcon sx={{ fontSize: '32px', color: defaultMethod === 'TOTP' ? '#fff' : '#1976d2' }} />
          <Typography variant="h6" sx={{ fontWeight: 600, color: defaultMethod === 'TOTP' ? '#fff' : '#1976d2', fontSize: '14px', textTransform: 'uppercase' }}>
            {t('sentences:pages.mfa_setup.method_totp') || 'Authenticator App (TOTP)'}
          </Typography>
          <Typography variant="body2" sx={{ color: defaultMethod === 'TOTP' ? '#fff' : 'text.secondary', textAlign: 'center', fontSize: '11px', textTransform: 'uppercase' }}>
            {t('sentences:pages.mfa_setup.method_totp_description') || 
              'Use an authenticator app like Google Authenticator. Free, works offline, more secure.'}
          </Typography>
        </Button>

        {/* SMS Option */}
        <Button
          variant={defaultMethod === 'SMS' ? "contained" : "outlined"}
          fullWidth
          onClick={() => {
            onSelectMethod('SMS');
            onClose();
          }}
          sx={{
            p: 2.5,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 1,
            borderColor: '#1976d2',
            backgroundColor: defaultMethod === 'SMS' ? '#1976d2' : 'transparent',
            color: defaultMethod === 'SMS' ? '#fff' : '#1976d2',
            '&:hover': {
              borderColor: '#1565c0',
              backgroundColor: defaultMethod === 'SMS' ? '#1565c0' : '#e3f2fd',
            },
          }}
        >
          <SmartphoneOutlinedIcon sx={{ fontSize: '32px', color: defaultMethod === 'SMS' ? '#fff' : '#1976d2' }} />
          <Typography variant="h6" sx={{ fontWeight: 600, color: defaultMethod === 'SMS' ? '#fff' : '#1976d2', fontSize: '14px', textTransform: 'uppercase' }}>
            {t('sentences:pages.mfa_setup.method_sms') || 'SMS (Text Message)'}
          </Typography>
          <Typography variant="body2" sx={{ color: 'text.secondary', textAlign: 'center', fontSize: '11px', textTransform: 'uppercase' }}>
            {t('sentences:pages.mfa_setup.method_sms_description') || 
              'Receive verification codes via text message. Requires phone number.'}
          </Typography>
        </Button>
      </Box>
    </>
  );

  // Use Popover if anchorEl is provided, otherwise use Dialog (centered)
  if (anchorEl) {
    return (
      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={canDisableCompanyMfa ? onClose : undefined}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
        PaperProps={{
          sx: {
            width: '400px',
            maxWidth: '90vw',
            mt: 1,
            borderRadius: 2,
            boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
          }
        }}
      >
        <Box sx={{ p: 3 }}>
          <Typography variant="h6" sx={{ fontWeight: 600, mb: 2, fontSize: '16px' }}>
            {t('sentences:pages.mfa_setup.choose_method_title') || 'Choose Your MFA Method'}
          </Typography>
          {methodOptions}
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
            {canDisableCompanyMfa ? (
              <Button onClick={onClose} size="small" sx={{ textTransform: 'uppercase' }}>
                {t('verbs.cancel')}
              </Button>
            ) : (
              <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: '11px', fontStyle: 'italic' }}>
                {t('sentences:pages.mfa_setup.mfa_required_message') || 'MFA setup is required. Please select a method to continue.'}
              </Typography>
            )}
          </Box>
        </Box>
      </Popover>
    );
  }

  // Fallback to centered Dialog if no anchor
  // If canDisableCompanyMfa is false, prevent closing (regular users must set up MFA)
  return (
    <Dialog 
      open={open} 
      onClose={canDisableCompanyMfa ? onClose : undefined} 
      maxWidth="sm" 
      fullWidth
      disableEscapeKeyDown={!canDisableCompanyMfa}
    >
      <DialogTitle>
        {t('sentences:pages.mfa_setup.choose_method_title') || 'Choose Your MFA Method'}
      </DialogTitle>
      <DialogContent>
        {methodOptions}
        {canDisableCompanyMfa && onDisableCompanyMfa && (
          <Box sx={{ mt: 3, pt: 2, borderTop: '1px solid', borderColor: 'divider' }}>
            <Typography variant="body2" sx={{ mb: 2, color: 'text.secondary' }}>
              {t('sentences:pages.mfa_setup.disable_company_mfa_description') || 'As an admin or owner, you can disable MFA for your company instead.'}
            </Typography>
            <Button
              variant="outlined"
              color="secondary"
              fullWidth
              onClick={onDisableCompanyMfa}
            >
              {t('sentences:pages.mfa_setup.disable_company_mfa_button') || 'Disable Company MFA'}
            </Button>
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        {canDisableCompanyMfa && (
          <Button onClick={onClose}>
            {t('verbs.cancel')}
          </Button>
        )}
        {!canDisableCompanyMfa && (
          <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: '11px', fontStyle: 'italic' }}>
            {t('sentences:pages.mfa_setup.mfa_required_message') || 'MFA setup is required. Please select a method to continue.'}
          </Typography>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default MfaMethodSelectionDialog;
